// features/reports/components/CostoDocente.tsx
// ============================================================================
// COSTO DOCENTE POR CLASE + NÓMINA PENDIENTE (bloque B4, 19-oct-2026) — sub-pestaña de Reportes › Pronóstico y presupuesto.
// Para Mabel: cuánto cuesta en maestros cada clase del mes (sesiones del horario × tarifa; sueldo fijo con clases
// incluidas repartido según el selector; comisión a la clase de la alumna) y cuánto falta por pagar de la nómina.
// No calcula nada: lo arma el servidor (/finances/presupuesto/costo-docente, lib/letsmoveCostoDocente.js) y cuadra con
// la nómina calculada de la Conciliación. El selector de reparto NO se guarda (el valor por defecto es el supuesto de la
// pregunta 9 a Mabel). Plan: forobeta-ops/letsmove/PLAN-FINANZAS-MABEL-BLOQUES-20261014.md (fila B4).
// ============================================================================
import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ChevronDown, FileSpreadsheet, Info } from 'lucide-react';
import { getCostoDocente, urlExportCostoDocente, CostoDocente as CD, RepartoSueldo } from '../api';
import { moneyEntero as money } from '../../finances/api';
import { Skeleton } from '../../../components/Skeleton';
import { readCache, writeCache } from '../../../services/localCache';

const card = 'rounded-2xl border border-line bg-surface p-4 shadow-sm';
const th = 'py-2 pr-2 text-left text-xs font-medium uppercase tracking-wide text-content-3';
const thR = `${th} text-right`;
const navBtn = 'rounded-lg border border-line bg-surface p-2 text-content-2 transition hover:bg-surface-2';
const REPARTO_LABEL: Record<RepartoSueldo, string> = { horas: 'por horas', sesiones: 'por sesiones', administrativo: 'como administrativo' };
type Orden = 'total' | 'porSesion' | 'porAlumna';
const ORDEN_LABEL: Record<Orden, string> = { total: 'Costo del mes', porSesion: 'Costo por sesión', porAlumna: 'Costo por alumna' };

export default function CostoDocente({ mes }: { mes: string }) {
  const [reparto, setReparto] = useState<RepartoSueldo | null>(null);   // null = el del servidor (supuesto por defecto)
  const clave = `rep-costo-docente-${mes}-${reparto ?? 'def'}`;
  const [d, setD] = useState<CD | null>(() => readCache<CD>(clave) ?? null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<{ status: number; msg: string } | null>(null);
  const [orden, setOrden] = useState<Orden>('total');
  const [verMaestros, setVerMaestros] = useState(false);
  const [verPendiente, setVerPendiente] = useState(true);

  useEffect(() => {
    let vivo = true;
    const cached = readCache<CD>(clave);
    setD(cached ?? null); setError(null); setCargando(true);
    getCostoDocente(mes, reparto)
      .then((r) => { if (!vivo) return; setD(r); writeCache(clave, r); })
      .catch((e: any) => { if (vivo) setError({ status: Number(e?.status || 0), msg: e?.message || 'No se pudo cargar el costo docente' }); })
      .finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
  }, [mes, reparto, clave]);

  const clases = useMemo(() => {
    if (!d) return [];
    const v = (c: CD['clases'][number]) => (orden === 'total' ? c.total : orden === 'porSesion' ? c.porSesion ?? -1 : c.porAlumna ?? -1);
    return [...d.clases].sort((a, b) => v(b) - v(a) || a.nombre.localeCompare(b.nombre));
  }, [d, orden]);

  if (error && !d) {
    return (
      <div className={`${card} mt-4 text-sm`}>
        {error.status === 404
          ? <p className="flex items-center gap-2 text-content-2"><Info size={16} className="text-brand-blue" /> El costo docente se activa cuando el servidor termine de actualizarse. Vuelve a intentar en unos minutos.</p>
          : <p className="text-red-700 dark:text-red-300">{error.msg}</p>}
      </div>
    );
  }
  if (!d) return <Skeleton className="mt-4 h-96 w-full rounded-2xl" />;

  const pp = d.pendientePago;
  const repActual = d.reparto;
  const maxTotal = Math.max(1, ...d.clases.map((c) => c.total));

  return (
    <div className={cargando ? 'opacity-60 transition' : 'transition'} data-testid="costo-docente">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-content-3">
          <b className="text-content-2">Costo docente de {d.titulo.toLowerCase()}</b>{d.enCurso ? ' (mes en curso)' : ''} · cuánto cuesta en maestros cada clase y cuánto falta por pagar de la nómina.
        </p>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-xs text-content-3">
            <span className="hidden sm:inline">Sueldo fijo con clases:</span><span className="sm:hidden">Sueldo fijo:</span>
            <select value={repActual} onChange={(e) => setReparto(e.target.value as RepartoSueldo)} data-testid="cd-reparto"
              className="rounded-lg border border-line bg-surface px-2 py-1.5 text-sm text-content outline-none focus:border-brand-blue">
              {d.repartos.map((r) => <option key={r.clave} value={r.clave}>{REPARTO_LABEL[r.clave]}{r.clave === d.repartoDefault ? ' (supuesto)' : ''}</option>)}
            </select>
          </label>
          <a href={urlExportCostoDocente(mes, reparto)} download title="Descargar Excel (por clase, por maestro y nómina pendiente)" className={`${navBtn} flex items-center gap-1.5 text-sm`} data-testid="cd-excel">
            <FileSpreadsheet size={16} /> <span className="hidden sm:inline">Excel</span>
          </a>
        </div>
      </div>

      {d.parcial && (
        <div className="mb-3 flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" /> <span><b>Mes parcial.</b> {d.parcial}</span>
        </div>
      )}

      {/* KPI */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div className={card}>
          <div className="text-xs font-medium uppercase tracking-wide text-content-3">Costo docente en clases</div>
          <div className="mt-1 font-display text-xl font-bold text-content" data-testid="cd-asignado">{money(d.totales.asignado)}</div>
          <div className="mt-0.5 text-xs text-content-3">{d.totales.clases} clases · {d.totales.horas} h</div>
        </div>
        <div className={card}>
          <div className="text-xs font-medium uppercase tracking-wide text-content-3">Por sesión (promedio)</div>
          <div className="mt-1 font-display text-xl font-bold text-content">{d.totales.porSesion == null ? '—' : money(d.totales.porSesion)}</div>
          <div className="mt-0.5 text-xs text-content-3">{d.totales.sesiones} dadas{d.totales.futuras ? ` + ${d.totales.futuras} programadas` : ''}</div>
        </div>
        <div className={card}>
          <div className="text-xs font-medium uppercase tracking-wide text-content-3">Nómina docente del mes</div>
          <div className="mt-1 font-display text-xl font-bold text-content">{money(d.totales.nominaDocente)}</div>
          <div className="mt-0.5 text-xs text-content-3">{d.totales.noAsignado ? `${money(d.totales.noAsignado)} sin clase (bonos/ajustes)` : 'todo asignado a clases'}</div>
        </div>
        <div className={card}>
          <div className="text-xs font-medium uppercase tracking-wide text-content-3">Nómina pendiente de pago</div>
          <div className={`mt-1 font-display text-xl font-bold ${pp.vencida > 0.005 ? 'text-red-700 dark:text-red-300' : 'text-content'}`} data-testid="cd-pendiente">{money(pp.total)}</div>
          <div className="mt-0.5 text-xs text-content-3">{pp.vencida > 0.005 ? <span className="text-red-700 dark:text-red-300">{money(pp.vencida)} de semanas cerradas</span> : 'semanas cerradas: al día'}{pp.enCurso > 0.005 ? ` · ${money(pp.enCurso)} en curso` : ''}</div>
        </div>
      </div>

      {d.avisos.length > 0 && (
        <div className="mt-3 space-y-1 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200" data-testid="cd-avisos">
          {d.avisos.map((a) => <p key={a} className="flex items-start gap-2"><AlertTriangle size={15} className="mt-0.5 shrink-0" /> {a}</p>)}
        </div>
      )}

      {/* Por clase */}
      <div className={`${card} mt-4`}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-display text-base font-bold text-content">Costo por clase</h3>
          <div className="inline-flex max-w-full overflow-x-auto rounded-lg border border-line bg-surface p-0.5 text-xs" role="tablist" aria-label="Ordenar por">
            {(Object.keys(ORDEN_LABEL) as Orden[]).map((k) => (
              <button key={k} role="tab" aria-selected={orden === k} onClick={() => setOrden(k)} data-testid={`cd-orden-${k}`}
                className={`whitespace-nowrap rounded-md px-2.5 py-1 transition ${orden === k ? 'bg-accent text-on-accent shadow-sm' : 'text-content-2 hover:text-content'}`}>{ORDEN_LABEL[k]}</button>
            ))}
          </div>
        </div>
        {/* Escritorio: tabla */}
        <div className="mt-2 hidden overflow-x-auto md:block">
          <table className="w-full min-w-[760px] text-sm">
            <thead><tr className="border-b border-line"><th className={th}>Clase</th><th className={thR}>Sesiones</th><th className={thR}>Horas</th><th className={thR}>Por hora</th><th className={thR}>Sueldo fijo</th><th className={thR}>Comisión</th><th className={thR}>Costo del mes</th><th className={thR}>Por sesión</th><th className={thR}>Alumnas</th><th className={thR}>Por alumna</th></tr></thead>
            <tbody>
              {clases.map((c) => (
                <tr key={c.class_id} className="border-b border-line/60" data-testid={`cd-clase-${c.class_id}`}>
                  <td className="py-1.5 pr-2">
                    <div className="text-content">{c.nombre}{c.archivada && <span className="ml-1 rounded bg-amber-100 px-1 text-[10px] font-semibold text-amber-800 dark:bg-amber-900/50 dark:text-amber-200">archivada</span>}</div>
                    <div className="text-xs text-content-3">{c.profesores}{c.suplencias ? ` · ${c.suplencias} suplencia${c.suplencias === 1 ? '' : 's'}` : ''}{c.noDadas ? ` · ${c.noDadas} no se dio` : ''}</div>
                  </td>
                  <td className="py-1.5 pr-2 text-right tabular-nums text-content-2">{c.sesiones}{c.futuras ? <span className="text-xs text-content-3"> +{c.futuras}</span> : ''}</td>
                  <td className="py-1.5 pr-2 text-right tabular-nums text-content-3">{c.horas}</td>
                  <td className="py-1.5 pr-2 text-right tabular-nums text-content-2">{c.porHora ? money(c.porHora) : '—'}</td>
                  <td className="py-1.5 pr-2 text-right tabular-nums text-content-2">{c.sueldoFijo ? money(c.sueldoFijo) : '—'}</td>
                  <td className="py-1.5 pr-2 text-right tabular-nums text-content-2">{c.comision ? money(c.comision) : '—'}</td>
                  <td className="py-1.5 pr-2 text-right font-semibold tabular-nums text-content">
                    {money(c.total)}
                    <div className="ml-auto mt-0.5 h-1 rounded-full bg-brand-blue/70" style={{ width: `${Math.max(4, (c.total / maxTotal) * 100)}%` }} />
                  </td>
                  <td className="py-1.5 pr-2 text-right tabular-nums text-content-2">{c.porSesion == null ? '—' : money(c.porSesion)}</td>
                  <td className="py-1.5 pr-2 text-right tabular-nums text-content-3">{c.alumnas || '—'}</td>
                  <td className="py-1.5 text-right tabular-nums text-content-2">{c.porAlumna == null ? '—' : money(c.porAlumna)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-line font-semibold">
                <td className="py-2 pr-2 text-content">Total en clases</td>
                <td className="py-2 pr-2 text-right tabular-nums text-content-2">{d.totales.sesiones}{d.totales.futuras ? <span className="text-xs text-content-3"> +{d.totales.futuras}</span> : ''}</td>
                <td className="py-2 pr-2 text-right tabular-nums text-content-3">{d.totales.horas}</td>
                <td className="py-2 pr-2 text-right tabular-nums text-content-2">{money(d.clases.reduce((s, c) => s + c.porHora, 0))}</td>
                <td className="py-2 pr-2 text-right tabular-nums text-content-2">{money(d.clases.reduce((s, c) => s + c.sueldoFijo, 0))}</td>
                <td className="py-2 pr-2 text-right tabular-nums text-content-2">{money(d.clases.reduce((s, c) => s + c.comision, 0))}</td>
                <td className="py-2 pr-2 text-right tabular-nums text-content">{money(d.totales.asignado)}</td>
                <td className="py-2 pr-2 text-right tabular-nums text-content-2">{d.totales.porSesion == null ? '—' : money(d.totales.porSesion)}</td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          </table>
        </div>
        {/* Celular: tarjetas */}
        <ul className="mt-2 divide-y divide-line/60 md:hidden" data-testid="cd-lista-movil">
          {clases.map((c) => (
            <li key={c.class_id} className="py-2">
              <div className="flex items-baseline justify-between gap-2">
                <span className="min-w-0 truncate font-medium text-content">{c.nombre}{c.archivada && <span className="ml-1 rounded bg-amber-100 px-1 text-[10px] font-semibold text-amber-800 dark:bg-amber-900/50 dark:text-amber-200">archivada</span>}</span>
                <span className="shrink-0 font-semibold tabular-nums text-content">{money(c.total)}</span>
              </div>
              <div className="mt-0.5 flex items-baseline justify-between gap-2 text-xs text-content-3">
                <span className="min-w-0 truncate">{c.profesores} · {c.sesiones}{c.futuras ? `+${c.futuras}` : ''} ses. · {c.horas} h</span>
                <span className="shrink-0 tabular-nums">{c.porSesion == null ? '' : `${money(c.porSesion)}/ses.`}{c.porAlumna == null ? '' : ` · ${money(c.porAlumna)}/alumna`}</span>
              </div>
              <div className="mt-1 h-1 rounded-full bg-brand-blue/70" style={{ width: `${Math.max(4, (c.total / maxTotal) * 100)}%` }} />
            </li>
          ))}
        </ul>
        <p className="mt-2 text-xs text-content-3">
          Por hora = sesiones del horario × tarifa (sin las «no se dio»; la suplencia con la tarifa de quien la dio). Sueldo fijo = el de quien tiene clases incluidas en su sueldo, repartido <b>{REPARTO_LABEL[repActual]}</b>. Comisión = la del mes, a la clase de la alumna que pagó.
          {d.enCurso && d.totales.futuras ? ` «+N» = sesiones programadas que aún no pasan (el mes en curso cuenta con todo su horario).` : ''} Alumnas = activas inscritas hoy.
        </p>
      </div>

      {/* Nómina pendiente */}
      <div className={`${card} mt-4`}>
        <button onClick={() => setVerPendiente(!verPendiente)} className="flex w-full items-center justify-between gap-2 text-left">
          <h3 className="font-display text-base font-bold text-content">Nómina pendiente de pago</h3>
          <span className="flex items-center gap-2 text-xs text-content-3">{pp.pendientes} persona-semana · {money(pp.total)} <ChevronDown size={16} className={`shrink-0 transition ${verPendiente ? 'rotate-180' : ''}`} /></span>
        </button>
        {verPendiente && (
          <div className="mt-3 space-y-3 text-sm" data-testid="cd-pendiente-detalle">
            {pp.porSemana.map((w) => (
              <div key={w.from}>
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-medium text-content">{w.label}</span>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${w.total < 0.005 ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300' : w.vencida ? 'bg-red-500/10 text-red-700 dark:text-red-300' : 'bg-surface-2 text-content-2'}`}>
                    {w.total < 0.005 ? 'Pagada' : w.vencida ? `Pendiente · ${money(w.total)}` : `En curso · ${money(w.total)}`}
                  </span>
                </div>
                {w.personas.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {w.personas.map((p) => <span key={p.user_id} className="rounded-lg bg-surface-2 px-2 py-0.5 text-xs text-content-2">{p.nombre.split(' ').slice(0, 2).join(' ')} <b className="tabular-nums text-content">{money(p.monto)}</b></span>)}
                  </div>
                )}
              </div>
            ))}
            {pp.comision > 0.005 && <p className="text-xs text-content-3">Además, comisión por entregar en caja: <b className="text-content-2">{money(pp.comision)}</b>.</p>}
            <p className="text-xs text-content-3">{pp.explica} Se paga en Finanzas › Nómina › «Registrar pago». Incluye al staff administrativo (aquí no se reparte por clase).</p>
          </div>
        )}
      </div>

      {/* Por maestro + no asignado */}
      <div className={`${card} mt-4`}>
        <button onClick={() => setVerMaestros(!verMaestros)} className="flex w-full items-center justify-between gap-2 text-left">
          <h3 className="font-display text-base font-bold text-content">Por maestro (cuadre con la Conciliación)</h3>
          <span className="flex items-center gap-2 text-xs text-content-3">{d.personas.length} maestros · {money(d.totales.nominaDocente)} <ChevronDown size={16} className={`shrink-0 transition ${verMaestros ? 'rotate-180' : ''}`} /></span>
        </button>
        {verMaestros && (
          <>
            <div className="mt-2 overflow-x-auto">
              <table className="w-full min-w-[640px] text-sm">
                <thead><tr className="border-b border-line"><th className={th}>Maestro</th><th className={thR}>Sesiones</th><th className={thR}>Por hora</th><th className={thR}>Sueldo fijo</th><th className={thR}>Comisión</th><th className={thR}>Bonos y ajustes</th><th className={thR}>Calculado</th></tr></thead>
                <tbody>
                  {d.personas.map((p) => (
                    <tr key={p.user_id} className="border-b border-line/60">
                      <td className="py-1.5 pr-2"><div className="text-content">{p.nombre}</div><div className="text-xs text-content-3">{p.modo}{p.sueldoA === 'administrativo' ? ' · sueldo como administrativo' : ''}</div></td>
                      <td className="py-1.5 pr-2 text-right tabular-nums text-content-3">{p.sesiones}</td>
                      <td className="py-1.5 pr-2 text-right tabular-nums text-content-2">{p.horario ? money(p.horario) : '—'}</td>
                      <td className="py-1.5 pr-2 text-right tabular-nums text-content-2">{p.sueldoFijo ? money(p.sueldoFijo) : '—'}</td>
                      <td className="py-1.5 pr-2 text-right tabular-nums text-content-2">{p.comision ? money(p.comision) : '—'}</td>
                      <td className="py-1.5 pr-2 text-right tabular-nums text-content-2">{p.bonos + p.ajustes ? money(p.bonos + p.ajustes) : '—'}</td>
                      <td className={`py-1.5 text-right font-semibold tabular-nums ${p.cuadra ? 'text-content' : 'text-red-700 dark:text-red-300'}`} title={p.cuadra ? 'Igual que en la Conciliación' : `Conciliación: ${money(p.calculadoConciliacion)}`}>{money(p.calculado)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-xs text-content-3">«Calculado» es el mismo de la Conciliación. Los bonos y ajustes de la semana ({money(d.noAsignado.bonos + d.noAsignado.ajustes)}) no se cargan a ninguna clase. El staff sin clases (Karla, Mabel, recepción) es nómina administrativa y no aparece aquí.</p>
          </>
        )}
      </div>

      <div className={`${card} mt-4`}>
        <h3 className="font-display text-sm font-bold text-content">Supuestos</h3>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-content-3">{d.supuestos.map((s) => <li key={s}>{s}</li>)}<li>{d.regla.texto}</li></ul>
        <p className="mt-2 text-xs text-content-3">Generado el {d.generado}.</p>
      </div>
    </div>
  );
}
